// src/layouts/ProfileRailCard.tsx
//
// Cross-cutting rule: the user profile lives at the BOTTOM of the sidebar
// rail (as in roommatik.console.ui), not in the app bar. A recessed plate —
// one step deeper than the rail, not a card — with the avatar, name, role
// and a chevron opening a menu with the session countdown, change-password
// (local credentials only) and sign-out (hybrid-3c.md §4 "Profile block").
import React, { useCallback, useEffect, useState } from 'react';
import { useGetIdentity, useLogout, useSidebarState, useTranslate } from 'react-admin';
import {
    Avatar,
    Box,
    Chip,
    Divider,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    AccessTime as TimeIcon,
    Lock as LockIcon,
    Logout as LogoutIcon,
    UnfoldMoreRounded as UnfoldMoreIcon,
} from '@mui/icons-material';
import { decodeJWT, hasLocalCredentials } from '../utils/auth';
import { isDocumentationOnly } from '@/features/developers/access';
import ChangePasswordDialog from '../components/dialogs/ChangePasswordDialog';

/** The person's name as the identity provider states it; the account name
 *  (without its mail domain) only when no name is on the token. */
const displayNameOf = (identity: { fullName?: string; email?: string } | undefined): string => {
    const name = identity?.fullName?.trim();
    if (!name) return '';
    return name.includes('@') ? name.split('@')[0] : name;
};

const getInitials = (name?: string): string => {
    if (!name) return '?';
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0].toUpperCase())
        .join('');
};

export const ProfileRailCard: React.FC = () => {
    const theme = useTheme();
    const translate = useTranslate();
    const logout = useLogout();
    const { data: identity } = useGetIdentity();
    const [isSidebarOpen] = useSidebarState();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const open = isSidebarOpen || isSmallScreen;

    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [changePasswordOpen, setChangePasswordOpen] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState<string>('');
    const [isExpiringSoon, setIsExpiringSoon] = useState(false);
    const showChangePassword = hasLocalCredentials();

    const formatTimeRemaining = useCallback((seconds: number): string => {
        const days = Math.floor(seconds / 86400);
        const hours = Math.floor((seconds % 86400) / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        if (days > 0) return `${days}d ${hours}h`;
        if (hours > 0) return `${hours}h ${minutes}m`;
        if (minutes > 0) return `${minutes}m ${secs}s`;
        return `${secs}s`;
    }, []);

    const calculateTimeRemaining = useCallback(() => {
        const token = localStorage.getItem('token');
        if (!token) { setTimeRemaining(''); return; }
        try {
            const decoded = decodeJWT(token);
            if (!decoded || !decoded.exp) { setTimeRemaining(''); return; }
            const timeLeft = decoded.exp - Math.floor(Date.now() / 1000);
            if (timeLeft <= 0) {
                setTimeRemaining(translate('auth.session_expired', 'Sesión expirada'));
                setIsExpiringSoon(true);
                return;
            }
            setIsExpiringSoon(timeLeft <= 300);
            setTimeRemaining(formatTimeRemaining(timeLeft));
        } catch {
            setTimeRemaining('');
        }
    }, [formatTimeRemaining, translate]);

    useEffect(() => {
        calculateTimeRemaining();
        const interval = setInterval(calculateTimeRemaining, 1000);
        return () => clearInterval(interval);
    }, [calculateTimeRemaining]);

    const userName = displayNameOf(identity) || translate('resources.users.roles.User');
    const userRole = identity?.role || 'User';
    const roleLabel = isDocumentationOnly()
        ? translate('developers.role')
        : translate(`resources.users.roles.${userRole}`, { _: translate('resources.users.roles.User') });
    const profile = theme.custom.menu.profile;

    const openMenu = useCallback((target: HTMLElement) => setAnchor(target), []);
    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openMenu(e.currentTarget);
        }
    }, [openMenu]);

    const trigger = (
        <Box
            role="button"
            aria-haspopup="menu"
            aria-expanded={Boolean(anchor)}
            tabIndex={0}
            onClick={(e) => openMenu(e.currentTarget)}
            onKeyDown={handleKeyDown}
            sx={{
                mt: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                padding: open ? '8px 10px' : '8px',
                justifyContent: open ? 'flex-start' : 'center',
                // A recessed plate — one step deeper than the rail, not a
                // card lifted off it (hybrid-3c.md §4 "Profile block").
                // Collapsed: avatar only, no plate, no border, no padding.
                ...(open && {
                    backgroundColor: profile.bg,
                    border: `1px solid ${profile.border}`,
                    borderRadius: profile.borderRadius,
                }),
                cursor: 'pointer',
                transition: 'background-color .15s, border-color .15s',
                ...(open && { '&:hover': { backgroundColor: profile.hoverBg } }),
            }}
        >
            <Avatar
                sx={{
                    width: profile.avatarSize,
                    height: profile.avatarSize,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    backgroundColor: profile.avatarBg,
                    color: typeof profile.avatarColor === 'function' ? profile.avatarColor(theme) : profile.avatarColor,
                }}
            >
                {getInitials(userName)}
            </Avatar>
            {open && (
                <>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography noWrap sx={{ fontSize: profile.nameFontSize, fontWeight: profile.nameFontWeight, color: profile.nameColor }}>
                            {userName}
                        </Typography>
                        <Typography noWrap sx={{ fontSize: profile.roleFontSize, fontWeight: profile.roleFontWeight, color: profile.roleColor }}>
                            {roleLabel}
                        </Typography>
                    </Box>
                    <UnfoldMoreIcon sx={{ fontSize: '18px', color: profile.chevronColor }} />
                </>
            )}
        </Box>
    );

    return (
        <>
            {/* Tooltip only when collapsed — open, the name and role are
                already on screen (hybrid-3c.md §4 "Profile block"). */}
            {open ? trigger : (
                <Tooltip title={`${userName} · ${roleLabel}`} placement="right">
                    {trigger}
                </Tooltip>
            )}

            <Menu
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                slotProps={{ paper: { sx: { width: `calc(${theme.custom.menu.openWidth} - 20px)` } } }}
            >
                {/* Non-clickable header (hybrid-3c.md §4 "Profile block"). */}
                <Box sx={{ px: 2, py: 1 }}>
                    <Typography noWrap sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.text.primary }}>
                        {userName}
                    </Typography>
                    {identity?.email && (
                        <Typography noWrap sx={{ fontSize: '13px', color: theme.palette.text.secondary }}>
                            {identity.email}
                        </Typography>
                    )}
                    <Typography noWrap sx={{ fontSize: '13px', color: theme.palette.text.secondary }}>
                        {roleLabel}
                    </Typography>
                </Box>
                <Divider />
                {timeRemaining && (
                    <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'center' }}>
                        <Chip
                            icon={<TimeIcon />}
                            label={timeRemaining}
                            size="small"
                            color={isExpiringSoon ? 'warning' : 'success'}
                            sx={{ fontWeight: 500, '& .MuiChip-icon': { fontSize: 16 } }}
                        />
                    </Box>
                )}
                {timeRemaining && <Divider sx={{ mb: 0.5 }} />}
                {showChangePassword && (
                    <MenuItem
                        onClick={() => {
                            setAnchor(null);
                            setChangePasswordOpen(true);
                        }}
                    >
                        <ListItemIcon>
                            <LockIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                            {translate('resources.users.actions.change_password', 'Cambiar contraseña')}
                        </ListItemText>
                    </MenuItem>
                )}
                <MenuItem
                    onClick={() => {
                        setAnchor(null);
                        logout();
                    }}
                >
                    <ListItemIcon>
                        <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{translate('ra.auth.logout', 'Cerrar sesión')}</ListItemText>
                </MenuItem>
            </Menu>

            <ChangePasswordDialog
                open={changePasswordOpen}
                onClose={() => setChangePasswordOpen(false)}
                isCurrentUser={true}
            />
        </>
    );
};

export default ProfileRailCard;
